import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useResizableColumns } from "@/hooks/use-resizable-columns";
import { useColumnOrder } from "@/hooks/use-column-order";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { SpreadsheetCell } from "./spreadsheet-cell";
import { CustomerRecordDialog } from "./customer-record-dialog";
import {
  Plus, Loader2, Settings, Trash2, Pencil, Check, X,
  Undo2, Redo2, ChevronDown, Mail, Phone, Bell, Eye,
  GripVertical, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown,
  Edit, Send, GitMerge, Building2, DollarSign, Clock,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { PlatformRegion } from "@shared/schema";

const STATUS_OPTIONS = [
  { value: "trial", label: "On Trial" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "past_due", label: "Past Due" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS: Record<string, string> = {
  trial: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
  active: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
  paused: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
  past_due: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
  churned: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
};

type Customer = {
  id: string; recordId?: string | null; companyName: string; contactName?: string | null;
  phoneNumber?: string | null; emailAddress?: string | null; address?: string | null;
  city?: string | null; state?: string | null; zipCode?: string | null;
  website?: string | null; businessType?: string | null; region?: string | null;
  rep?: string | null; status: string; planTier?: string | null;
  baseRateCents: number; discountPercent: string; mrrCents: number;
  notes?: string | null; emailOptedOut: boolean; lastContacted?: string | null;
  workspaceSlug?: string | null; workspaceId: string;
  createdAt: string; updatedAt: string;
};

type UndoAction =
  | { type: "edit"; customerId: string; field: string; oldValue: any; newValue: any }
  | { type: "delete"; customer: Customer };

const getColumns = (businessTypes: string[]) => [
  { key: "companyName", label: "Company Name", width: "200px" },
  { key: "contactName", label: "Contact Name", width: "160px" },
  { key: "phoneNumber", label: "Phone", width: "140px", type: "phone" },
  { key: "emailAddress", label: "Email", width: "200px" },
  { key: "status", label: "Status", width: "110px", type: "status" },
  { key: "planTier", label: "Plan", width: "100px", type: "select", options: ["lite", "pro", "enterprise"] },
  { key: "mrrDisplay", label: "MRR", width: "100px", type: "readonly" },
  { key: "region", label: "Region", width: "140px", type: "region" },
  { key: "businessType", label: "Business Type", width: "150px", type: "select", options: businessTypes },
  { key: "rep", label: "Rep", width: "130px" },
  { key: "lastContacted", label: "Last Contacted", width: "140px", type: "readonly" },
  { key: "address", label: "Address", width: "180px" },
  { key: "city", label: "City", width: "110px" },
  { key: "state", label: "State", width: "70px" },
  { key: "zipCode", label: "Zip", width: "80px" },
  { key: "website", label: "Website", width: "180px", type: "url" },
];

const SERVER_SORTABLE = new Set(["companyName", "status", "planTier", "region", "rep", "lastContacted", "createdAt"]);

export function CustomersSpreadsheet({ searchQuery = "" }: { searchQuery?: string }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Filter/sort state ─────────────────────────────────────────────────────
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedBusinessType, setSelectedBusinessType] = useState("all");
  const [regionSearch, setRegionSearch] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // ── Selection / editing state ──────────────────────────────────────────────
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ rowId: string; column: string } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [visibleRowCount, setVisibleRowCount] = useState(50);
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [redoStack, setRedoStack] = useState<UndoAction[]>([]);

  // ── Dialog state ──────────────────────────────────────────────────────────
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isLogCallOpen, setIsLogCallOpen] = useState(false);
  const [logCallNotes, setLogCallNotes] = useState("");
  const [isReminderOpen, setIsReminderOpen] = useState(false);
  const [reminderDate, setReminderDate] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notesCustomerId, setNotesCustomerId] = useState<string | null>(null);
  const [notesValue, setNotesValue] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [mergeKeepId, setMergeKeepId] = useState<string>("");
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [newCustomerWorkspaceId, setNewCustomerWorkspaceId] = useState("");
  const [newCustomerCompanyName, setNewCustomerCompanyName] = useState("");

  // Region manage dialog
  const [isRegionDialogOpen, setIsRegionDialogOpen] = useState(false);
  const [isManageRegionsOpen, setIsManageRegionsOpen] = useState(false);
  const [newRegionName, setNewRegionName] = useState("");
  const [editingRegionId, setEditingRegionId] = useState<string | null>(null);
  const [editingRegionName, setEditingRegionName] = useState("");
  const [manageRegionSearch, setManageRegionSearch] = useState("");

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: regions = [] } = useQuery<PlatformRegion[]>({
    queryKey: ["/api/platform/regions"],
  });

  const { data: platformBizTypes = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/platform/business-types"],
  });
  const businessTypes = platformBizTypes.map((t) => t.name);

  const { data: workspaces = [] } = useQuery<{ id: string; name: string; slug: string }[]>({
    queryKey: ["/api/platform/workspaces-list"],
    queryFn: async () => {
      const r = await fetch("/api/platform/workspaces?limit=500", { credentials: "include" });
      if (!r.ok) return [];
      const d = await r.json();
      return (d.workspaces || d || []).map((w: any) => ({ id: w.id, name: w.name, slug: w.slug }));
    },
  });

  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 250);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const queryKey = useMemo(() => {
    const p: Record<string, string> = {};
    if (debouncedSearch) p.q = debouncedSearch;
    if (selectedRegion !== "all") p.region = selectedRegion;
    if (selectedStatus !== "all") p.status = selectedStatus;
    if (selectedBusinessType !== "all") p.businessType = selectedBusinessType;
    return ["/api/platform/customers", p] as const;
  }, [debouncedSearch, selectedRegion, selectedStatus, selectedBusinessType]);

  const { data: customers = [], isLoading, isError, refetch } = useQuery<Customer[]>({
    queryKey,
    queryFn: async () => {
      const url = new URL("/api/platform/customers", window.location.origin);
      const [, params] = queryKey;
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
      const r = await fetch(url.toString(), { credentials: "include" });
      if (!r.ok) throw new Error("Failed");
      return r.json();
    },
    staleTime: 30_000,
  });

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["/api/platform/customers"] });
  }, [qc]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  const sortedCustomers = useMemo(() => {
    if (!sortField) return customers;
    return [...customers].sort((a, b) => {
      const av = ((a as any)[sortField] ?? "") as string;
      const bv = ((b as any)[sortField] ?? "") as string;
      const cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [customers, sortField, sortDir]);

  const emptyRows = useMemo(() => {
    const n = Math.max(0, visibleRowCount - sortedCustomers.length);
    return Array.from({ length: n }, (_, i) => ({ id: `empty-${i}` }));
  }, [sortedCustomers.length, visibleRowCount]);

  // ── Column config ─────────────────────────────────────────────────────────
  const COLS = useMemo(() => getColumns(businessTypes), [businessTypes]);
  const { getColumnWidth, handleResizeStart } = useResizableColumns({ columns: COLS, storageKey: "customers-spreadsheet-col-widths" });
  const { orderedKeys, moveColumn } = useColumnOrder({ columnKeys: COLS.map(c => c.key), storageKey: "customers-spreadsheet-col-order" });
  const COLUMNS = useMemo(() => {
    const map = new Map(COLS.map(c => [c.key, c] as const));
    return orderedKeys.map(k => map.get(k)!).filter(Boolean);
  }, [orderedKeys, COLS]);
  const [draggedColKey, setDraggedColKey] = useState<string | null>(null);
  const [dragOverColKey, setDragOverColKey] = useState<string | null>(null);

  // ── Virtualizer ───────────────────────────────────────────────────────────
  const rowVirtualizer = useVirtualizer({
    count: sortedCustomers.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 28,
    overscan: 10,
  });

  const selectedCustomer = selectedRowId ? customers.find(c => c.id === selectedRowId) : null;
  const filteredRegions = useMemo(() => regions.filter(r => r.name.toLowerCase().includes(regionSearch.toLowerCase())), [regions, regionSearch]);
  const filteredManageRegions = useMemo(() => regions.filter(r => r.name.toLowerCase().includes(manageRegionSearch.toLowerCase())), [regions, manageRegionSearch]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: object }) =>
      apiRequest("PATCH", `/api/platform/customers/${id}`, updates),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/customers/${id}`),
    onSuccess: () => { invalidate(); setSelectedRowId(null); toast({ title: "Customer deleted" }); },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  const activityMutation = useMutation({
    mutationFn: ({ id, type, notes }: { id: string; type: string; notes: string }) =>
      apiRequest("POST", `/api/platform/customers/${id}/activities`, { type, notes }),
    onSuccess: () => {
      invalidate();
      setIsLogCallOpen(false);
      setIsReminderOpen(false);
      setLogCallNotes("");
      toast({ title: "Activity logged" });
    },
    onError: () => toast({ title: "Failed to log", variant: "destructive" }),
  });

  const mergeMutation = useMutation({
    mutationFn: ({ keepId, mergeId }: { keepId: string; mergeId: string }) =>
      apiRequest("POST", "/api/platform/customers/merge", { keepId, mergeId }),
    onSuccess: () => {
      invalidate();
      setIsMergeOpen(false);
      setSelectedIds(new Set());
      toast({ title: "Records merged (workspaces unchanged)" });
    },
    onError: () => toast({ title: "Failed to merge", variant: "destructive" }),
  });

  const bulkMutation = useMutation({
    mutationFn: ({ ids, action, fields }: { ids: string[]; action: string; fields?: Record<string, string> }) =>
      apiRequest("POST", "/api/platform/customers/bulk-action", { ids, action, fields }),
    onSuccess: () => {
      invalidate();
      setSelectedIds(new Set());
      setIsBulkDeleteOpen(false);
      setIsBulkEditOpen(false);
      toast({ title: "Done" });
    },
    onError: () => toast({ title: "Failed", variant: "destructive" }),
  });

  const createMutation = useMutation({
    mutationFn: (data: { workspaceId: string; companyName: string }) =>
      apiRequest("POST", "/api/platform/customers", { ...data, status: "trial", baseRateCents: 0 }),
    onSuccess: async (res) => {
      invalidate();
      const c = await res.json();
      setSelectedRowId(c.id);
      setIsAddCustomerOpen(false);
      setNewCustomerWorkspaceId("");
      setNewCustomerCompanyName("");
      toast({ title: "Customer created" });
    },
    onError: async (err: any) => {
      const body = err?.response ? await err.response.json().catch(() => ({})) : {};
      toast({ title: body?.message || "Failed to create customer", variant: "destructive" });
    },
  });

  // Region mutations
  const createRegionMutation = useMutation({
    mutationFn: (name: string) => apiRequest("POST", "/api/platform/regions", { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/platform/regions"] }); setNewRegionName(""); setIsRegionDialogOpen(false); toast({ title: "Region created" }); },
    onError: () => toast({ title: "Failed to create region", variant: "destructive" }),
  });

  const updateRegionMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => apiRequest("PATCH", `/api/platform/regions/${id}`, { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/platform/regions"] }); setEditingRegionId(null); },
    onError: () => toast({ title: "Failed to update region", variant: "destructive" }),
  });

  const deleteRegionMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/platform/regions/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/platform/regions"] }); toast({ title: "Region deleted" }); },
    onError: (err: any) => toast({ title: err?.message || "Cannot delete — customers assigned", variant: "destructive" }),
  });

  // ── Cell handling ─────────────────────────────────────────────────────────
  const handleCellChange = (customerId: string, column: string, value: any) => {
    if (customerId.startsWith("empty-")) return;
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      const oldValue = (customer as any)[column];
      setUndoStack(p => [...p, { type: "edit", customerId, field: column, oldValue, newValue: value }]);
      setRedoStack([]);
    }
    updateMutation.mutate({ id: customerId, updates: { [column]: value } });
  };

  const handleDeleteWithUndo = (id: string) => {
    const customer = customers.find(c => c.id === id);
    if (customer) { setUndoStack(p => [...p, { type: "delete", customer }]); setRedoStack([]); }
    deleteMutation.mutate(id);
  };

  const handleUndo = async () => {
    if (!undoStack.length) return;
    const action = undoStack[undoStack.length - 1];
    setUndoStack(p => p.slice(0, -1));
    if (action.type === "edit") {
      await apiRequest("PATCH", `/api/platform/customers/${action.customerId}`, { [action.field]: action.oldValue });
      setRedoStack(p => [...p, action]);
      invalidate();
      toast({ title: "Undone" });
    } else {
      const { id, ...data } = action.customer;
      await apiRequest("POST", "/api/platform/customers", { ...data, workspaceId: (action.customer as any).workspaceId });
      setRedoStack(p => [...p, action]);
      invalidate();
      toast({ title: "Undone — customer restored" });
    }
  };

  const handleRedo = async () => {
    if (!redoStack.length) return;
    const action = redoStack[redoStack.length - 1];
    setRedoStack(p => p.slice(0, -1));
    if (action.type === "edit") {
      await apiRequest("PATCH", `/api/platform/customers/${action.customerId}`, { [action.field]: action.newValue });
      setUndoStack(p => [...p, action]);
      invalidate();
      toast({ title: "Redone" });
    } else {
      await apiRequest("DELETE", `/api/platform/customers/${action.customer.id}`);
      setUndoStack(p => [...p, action]);
      invalidate();
      toast({ title: "Redone — customer deleted again" });
    }
  };

  const handleTabNavigation = (customerId: string, colKey: string, shiftKey: boolean) => {
    const editable = COLUMNS.filter(c => c.type !== "region" && c.type !== "select" && c.type !== "status" && c.type !== "readonly");
    const ci = editable.findIndex(c => c.key === colKey);
    const ri = sortedCustomers.findIndex(c => c.id === customerId);
    if (shiftKey) {
      if (ci > 0) setEditingCell({ rowId: customerId, column: editable[ci - 1].key });
      else if (ri > 0) setEditingCell({ rowId: sortedCustomers[ri - 1].id, column: editable[editable.length - 1].key });
    } else {
      if (ci < editable.length - 1) setEditingCell({ rowId: customerId, column: editable[ci + 1].key });
      else if (ri < sortedCustomers.length - 1) setEditingCell({ rowId: sortedCustomers[ri + 1].id, column: editable[0].key });
    }
  };

  const toggleSort = (field: string) => {
    if (!SERVER_SORTABLE.has(field)) return;
    if (sortField === field) { if (sortDir === "asc") setSortDir("desc"); else { setSortField(null); setSortDir("asc"); } }
    else { setSortField(field); setSortDir("asc"); }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (!SERVER_SORTABLE.has(field)) return null;
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const getRowClassName = (c: Customer) => {
    if (selectedRowId === c.id) return "spreadsheet-row-selected";
    if (c.status === "active") return "spreadsheet-row-booked";
    if (c.status === "trial") return "spreadsheet-row-interested";
    if (c.status === "paused") return "spreadsheet-row-not-now";
    if (c.status === "cancelled" || c.status === "churned") return "spreadsheet-row-disqualified";
    return "";
  };

  const mergeSelectedIds = [...selectedIds].slice(0, 2);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="border-b border-border bg-card overflow-x-auto">
        <div className="flex items-center gap-3 p-4 min-w-max flex-wrap">
          <Label className="text-sm font-medium">Region:</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-44 justify-between" data-testid="select-customer-region">
                <span className="truncate">{selectedRegion === "all" ? "All Regions" : selectedRegion}</span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="start">
              <div className="p-2 border-b">
                <Input placeholder="Search…" value={regionSearch} onChange={e => setRegionSearch(e.target.value)} className="h-8" />
              </div>
              <div className="max-h-56 overflow-auto p-2 space-y-1">
                <div className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer" onClick={() => setSelectedRegion("all")}>
                  <Checkbox checked={selectedRegion === "all"} onCheckedChange={() => setSelectedRegion("all")} onClick={e => e.stopPropagation()} />
                  <span className="text-sm font-medium">All Regions</span>
                </div>
                {filteredRegions.map(r => (
                  <div key={r.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer" onClick={() => setSelectedRegion(selectedRegion === r.name ? "all" : r.name)}>
                    <Checkbox checked={selectedRegion === r.name} onCheckedChange={v => setSelectedRegion(v ? r.name : "all")} onClick={e => e.stopPropagation()} data-testid={`checkbox-region-${r.id}`} />
                    <span className="text-sm">{r.name}</span>
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <Dialog open={isRegionDialogOpen} onOpenChange={setIsRegionDialogOpen}>
            <Button variant="outline" size="sm" onClick={() => setIsRegionDialogOpen(true)} data-testid="button-add-region-customer">
              <Plus className="w-4 h-4 mr-1" /> New Region
            </Button>
            <DialogContent>
              <DialogHeader><DialogTitle>Create New Region</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <Input value={newRegionName} onChange={e => setNewRegionName(e.target.value)} placeholder="e.g., Columbus, OH" data-testid="input-new-region" />
                <Button className="w-full" onClick={() => createRegionMutation.mutate(newRegionName.trim())} disabled={!newRegionName.trim() || createRegionMutation.isPending}>
                  {createRegionMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Region
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={isManageRegionsOpen} onOpenChange={open => { setIsManageRegionsOpen(open); if (!open) setManageRegionSearch(""); }}>
            <Button variant="outline" size="sm" onClick={() => setIsManageRegionsOpen(true)} data-testid="button-manage-regions-customer">
              <Settings className="w-4 h-4 mr-1" /> Manage Regions
            </Button>
            <DialogContent>
              <DialogHeader><DialogTitle>Manage Regions</DialogTitle></DialogHeader>
              <Input placeholder="Search…" value={manageRegionSearch} onChange={e => setManageRegionSearch(e.target.value)} className="mb-3" />
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {filteredManageRegions.map(region => (
                  <div key={region.id} className="flex items-center gap-2 p-2 border rounded-md">
                    {editingRegionId === region.id ? (
                      <>
                        <Input value={editingRegionName} onChange={e => setEditingRegionName(e.target.value)} className="h-8 flex-1" autoFocus />
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => updateRegionMutation.mutate({ id: region.id, name: editingRegionName })} disabled={!editingRegionName.trim()}><Check className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingRegionId(null)}><X className="w-3.5 h-3.5" /></Button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm">{region.name}</span>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingRegionId(region.id); setEditingRegionName(region.name); }}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteRegionMutation.mutate(region.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                  </div>
                ))}
                {regions.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No regions yet</p>}
              </div>
            </DialogContent>
          </Dialog>

          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
            <Label className="text-sm font-medium">Status:</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-36" data-testid="select-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
            <Label className="text-sm font-medium">Business Type:</Label>
            <Select value={selectedBusinessType} onValueChange={setSelectedBusinessType}>
              <SelectTrigger className="w-44" data-testid="select-biz-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Business Types</SelectItem>
                {businessTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1 ml-2">
            <Button variant="ghost" size="icon" onClick={handleUndo} disabled={!undoStack.length} className="h-8 w-8" title="Undo"><Undo2 className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={handleRedo} disabled={!redoStack.length} className="h-8 w-8" title="Redo"><Redo2 className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      {/* ── Action bar ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border bg-muted/50 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { if (selectedCustomer) { setEmailTo(selectedCustomer.emailAddress || ""); setEmailSubject(""); setEmailBody(""); setIsEmailOpen(true); } }} disabled={!selectedCustomer?.emailAddress} data-testid="button-email-customer">
            <Mail className="w-4 h-4 mr-1" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setLogCallNotes(""); setIsLogCallOpen(true); }} disabled={!selectedRowId || selectedRowId.startsWith("empty-")} data-testid="button-log-call-customer">
            <Phone className="w-4 h-4 mr-1" /> Log Call
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setReminderDate(""); setReminderNote(""); setIsReminderOpen(true); }} disabled={!selectedRowId || selectedRowId.startsWith("empty-")} data-testid="button-reminder-customer">
            <Bell className="w-4 h-4 mr-1" /> Set Reminder
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsDetailOpen(true)} disabled={!selectedRowId || selectedRowId.startsWith("empty-")} data-testid="button-activity-customer">
            <Eye className="w-4 h-4 mr-1" /> Activity
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!selectedRowId || selectedRowId.startsWith("empty-")} data-testid="dropdown-customer-actions">
                Customer Actions <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsDetailOpen(true)} data-testid="action-edit-customer">
                <Pencil className="w-4 h-4 mr-2" /> Edit
              </DropdownMenuItem>
              {selectedCustomer?.workspaceSlug && (
                <DropdownMenuItem asChild>
                  <a href={`/w/${selectedCustomer.workspaceSlug}`} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2 text-primary" /> Open Workspace
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {STATUS_OPTIONS.map(s => (
                <DropdownMenuItem key={s.value} onClick={() => { if (selectedRowId) updateMutation.mutate({ id: selectedRowId, updates: { status: s.value } }); }} data-testid={`action-status-${s.value}`}>
                  <span className={`w-2 h-2 rounded-full mr-2 inline-block ${STATUS_COLORS[s.value]?.split(" ")[0]}`} />
                  Mark {s.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => selectedRowId && setConfirmDeleteId(selectedRowId)} data-testid="action-delete-customer">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" onClick={() => setIsAddCustomerOpen(true)} data-testid="button-add-customer">
            <Plus className="w-4 h-4 mr-1" /> Add Customer
          </Button>
        </div>
      </div>

      {/* ── Bulk bar ───────────────────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary/10 border-b border-border flex-wrap">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Button variant="outline" size="sm" onClick={() => setIsBulkEditOpen(true)} data-testid="button-bulk-edit-customers"><Edit className="w-4 h-4 mr-1" /> Bulk Edit</Button>
          <Button variant="destructive" size="sm" onClick={() => setIsBulkDeleteOpen(true)} data-testid="button-bulk-delete-customers"><Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.size}</Button>
          {selectedIds.size === 2 && (
            <Button variant="outline" size="sm" onClick={() => { setMergeKeepId(mergeSelectedIds[0]); setIsMergeOpen(true); }} data-testid="button-merge-customers">
              <GitMerge className="w-4 h-4 mr-1" /> Merge Selected
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </div>
      )}

      {/* ── Spreadsheet ───────────────────────────────────────────────────── */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
            <p className="text-sm">Failed to load customers.</p>
            <Button size="sm" onClick={() => refetch()}>Retry</Button>
          </div>
        ) : (
          <div className="min-w-max">
            {/* Header */}
            <div className="flex sticky top-0 bg-muted border-b border-border z-10">
              <div className="w-8 shrink-0 border-r border-border flex items-center justify-center">
                <Checkbox
                  checked={sortedCustomers.length > 0 && sortedCustomers.every(c => selectedIds.has(c.id))}
                  onCheckedChange={checked => {
                    if (checked) setSelectedIds(new Set(sortedCustomers.map(c => c.id)));
                    else setSelectedIds(new Set());
                  }}
                  data-testid="checkbox-select-all-customers"
                />
              </div>
              {COLUMNS.map(col => (
                <div
                  key={col.key}
                  draggable
                  onDragStart={e => { setDraggedColKey(col.key); e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={e => { if (!draggedColKey || draggedColKey === col.key) return; e.preventDefault(); setDragOverColKey(col.key); }}
                  onDragLeave={() => { if (dragOverColKey === col.key) setDragOverColKey(null); }}
                  onDrop={e => { e.preventDefault(); const src = draggedColKey || e.dataTransfer.getData("text/plain"); if (src && src !== col.key) moveColumn(src, col.key); setDraggedColKey(null); setDragOverColKey(null); }}
                  onDragEnd={() => { setDraggedColKey(null); setDragOverColKey(null); }}
                  className={cn("spreadsheet-cell font-medium text-sm bg-muted relative group cursor-grab active:cursor-grabbing", draggedColKey === col.key && "opacity-40", dragOverColKey === col.key && draggedColKey !== col.key && "border-l-2 border-l-primary")}
                  style={{ width: getColumnWidth(col.key), minWidth: 50 }}
                  data-testid={`col-header-${col.key}`}
                >
                  <button onClick={() => toggleSort(col.key)} className="flex items-center hover:text-foreground w-full">
                    {col.label}<SortIcon field={col.key} />
                  </button>
                  <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary/50 group-hover:bg-border" onMouseDown={e => handleResizeStart(e, col.key)} />
                </div>
              ))}
            </div>

            {/* Rows (virtualized) */}
            <div style={{ height: rowVirtualizer.getTotalSize(), width: "100%", position: "relative" }}>
              {rowVirtualizer.getVirtualItems().map(vRow => {
                const customer = sortedCustomers[vRow.index];
                if (!customer) return null;
                return (
                  <div
                    key={customer.id}
                    data-index={vRow.index}
                    className={cn("flex border-b border-border hover:bg-muted/30 transition-colors", getRowClassName(customer), selectedIds.has(customer.id) && "bg-primary/10")}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${vRow.start}px)` }}
                  >
                    <div className="w-8 shrink-0 h-7 flex items-center justify-center border-r border-border bg-muted/50">
                      <Checkbox
                        checked={selectedIds.has(customer.id)}
                        onCheckedChange={checked => setSelectedIds(prev => { const n = new Set(prev); checked ? n.add(customer.id) : n.delete(customer.id); return n; })}
                        onClick={e => e.stopPropagation()}
                        data-testid={`checkbox-customer-${customer.id}`}
                      />
                    </div>
                    {COLUMNS.map(col => (
                      <div key={col.key} style={{ width: getColumnWidth(col.key), minWidth: 50 }}>
                        {col.type === "status" ? (
                          <Select value={customer.status} onValueChange={v => handleCellChange(customer.id, "status", v)}>
                            <SelectTrigger className="h-7 border-0 rounded-none text-xs focus:ring-0 focus:ring-offset-0 px-2">
                              <span className={cn("px-1.5 py-0.5 rounded text-xs font-medium", STATUS_COLORS[customer.status] || "bg-muted text-muted-foreground")}>
                                {STATUS_OPTIONS.find(s => s.value === customer.status)?.label || customer.status}
                              </span>
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : col.type === "region" ? (
                          <Select value={customer.region ?? ""} onValueChange={v => handleCellChange(customer.id, "region", v)}>
                            <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0">
                              <SelectValue placeholder="Select region" />
                            </SelectTrigger>
                            <SelectContent>
                              {regions.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : col.type === "select" && col.options ? (
                          <Select value={(customer as any)[col.key] || ""} onValueChange={v => handleCellChange(customer.id, col.key, v)}>
                            <SelectTrigger className="h-7 border-0 rounded-none text-sm focus:ring-0 focus:ring-offset-0">
                              <SelectValue placeholder={col.label} />
                            </SelectTrigger>
                            <SelectContent>
                              {col.options.map((opt: string) => <SelectItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : col.type === "readonly" ? (
                          <div className="spreadsheet-cell h-7 flex items-center px-2 text-sm text-muted-foreground bg-muted/20 border-r border-border truncate">
                            {col.key === "mrrDisplay"
                              ? `$${((customer.mrrCents || 0) / 100).toFixed(2)}`
                              : col.key === "lastContacted" && customer.lastContacted
                                ? new Date(customer.lastContacted).toLocaleDateString()
                                : (customer as any)[col.key] || "—"}
                          </div>
                        ) : col.key === "notes" && !(editingCell?.rowId === customer.id && editingCell?.column === "notes") ? (
                          <div
                            className="spreadsheet-cell h-7 flex items-center border-r border-border px-2 cursor-text overflow-hidden"
                            onDoubleClick={() => { setSelectedRowId(customer.id); setNotesCustomerId(customer.id); setNotesValue(customer.notes || ""); setIsNotesOpen(true); }}
                            onClick={() => { setSelectedRowId(customer.id); setEditingCell({ rowId: customer.id, column: "notes" }); }}
                            data-testid={`cell-notes-${customer.id}`}
                          >
                            <span className={cn("text-sm truncate w-full", !customer.notes && "text-muted-foreground/50")}>{customer.notes || "Add notes…"}</span>
                          </div>
                        ) : col.key === "website" && !(editingCell?.rowId === customer.id && editingCell?.column === "website") ? (
                          (() => {
                            const raw = (customer.website || "").trim();
                            const href = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
                            return (
                              <div className="spreadsheet-cell h-7 flex items-center gap-1 border-r border-border px-2 group">
                                {raw ? (
                                  <>
                                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary flex-shrink-0" onClick={e => e.stopPropagation()}><ExternalLink className="w-3.5 h-3.5" /></a>
                                    <button type="button" className="flex-1 text-left text-sm truncate text-muted-foreground hover:text-foreground" onClick={() => { setSelectedRowId(customer.id); setEditingCell({ rowId: customer.id, column: "website" }); }}>{raw}</button>
                                  </>
                                ) : (
                                  <button type="button" className="w-full text-left text-sm text-muted-foreground/50 hover:text-foreground" onClick={() => { setSelectedRowId(customer.id); setEditingCell({ rowId: customer.id, column: "website" }); }}>Add website…</button>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <SpreadsheetCell
                            value={(customer as any)[col.key] || ""}
                            onChange={v => handleCellChange(customer.id, col.key, v)}
                            isEditing={editingCell?.rowId === customer.id && editingCell?.column === col.key}
                            onStartEdit={() => { setSelectedRowId(customer.id); setEditingCell({ rowId: customer.id, column: col.key }); }}
                            onEndEdit={() => setEditingCell(null)}
                            onTab={shift => handleTabNavigation(customer.id, col.key, shift)}
                            onFocus={() => setSelectedRowId(customer.id)}
                            type={col.type as any}
                            placeholder={col.label}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Empty rows */}
            {emptyRows.map((row, idx) => (
              <div key={row.id} className={cn("flex border-b border-border", idx === 0 ? "opacity-40" : "opacity-20 pointer-events-none")}>
                <div className="w-8 shrink-0 h-7 border-r border-border bg-muted/50 flex items-center justify-center text-xs text-muted-foreground">{sortedCustomers.length + idx + 1}</div>
                {COLUMNS.map(col => <div key={col.key} style={{ width: getColumnWidth(col.key), minWidth: 50 }}><div className="h-7 border-r border-border" /></div>)}
              </div>
            ))}

            <div className="flex items-center justify-center py-1 border-b border-border bg-muted/30">
              <Button variant="ghost" size="sm" onClick={() => setVisibleRowCount(n => n + 10)} className="text-muted-foreground" data-testid="button-add-more-rows">+ Add 10 rows</Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Dialogs ────────────────────────────────────────────────────────── */}

      {/* CustomerRecordDialog (detail + activity) */}
      <CustomerRecordDialog
        customer={selectedCustomer || null}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onUpdated={updated => qc.invalidateQueries({ queryKey: ["/api/platform/customers"] })}
      />

      {/* Email */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader><DialogTitle>Email {selectedCustomer?.contactName || selectedCustomer?.companyName}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>To</Label><Input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} /></div>
            <div className="space-y-2"><Label>Subject</Label><Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} /></div>
            <div className="space-y-2"><Label>Message</Label><VoiceToTextTextarea value={emailBody} onChange={e => setEmailBody(e.target.value)} rows={6} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEmailOpen(false)}>Cancel</Button>
            <Button disabled={!emailTo || !emailSubject || !emailBody} onClick={() => { if (selectedRowId) activityMutation.mutate({ id: selectedRowId, type: "email", notes: `To: ${emailTo}\nSubject: ${emailSubject}\n\n${emailBody}` }); setIsEmailOpen(false); }}>
              <Send className="w-4 h-4 mr-2" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Call */}
      <Dialog open={isLogCallOpen} onOpenChange={setIsLogCallOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle>Log Call — {selectedCustomer?.contactName || selectedCustomer?.companyName}</DialogTitle></DialogHeader>
          <VoiceToTextTextarea value={logCallNotes} onChange={e => setLogCallNotes(e.target.value)} placeholder="Call notes…" rows={5} className="mt-4" autoFocus data-testid="textarea-log-call" />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setIsLogCallOpen(false)}>Cancel</Button>
            <Button onClick={() => { if (selectedRowId) activityMutation.mutate({ id: selectedRowId, type: "call", notes: logCallNotes }); }} disabled={!logCallNotes.trim() || activityMutation.isPending}>
              {activityMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Reminder */}
      <Dialog open={isReminderOpen} onOpenChange={setIsReminderOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader><DialogTitle>Set Reminder — {selectedCustomer?.contactName || selectedCustomer?.companyName}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={reminderDate} onChange={e => setReminderDate(e.target.value)} /></div>
            <div className="space-y-2"><Label>Note (optional)</Label><VoiceToTextTextarea value={reminderNote} onChange={e => setReminderNote(e.target.value)} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsReminderOpen(false)}>Cancel</Button>
            <Button disabled={!reminderDate} onClick={() => { if (selectedRowId) activityMutation.mutate({ id: selectedRowId, type: "note", notes: `[Reminder: ${reminderDate}] ${reminderNote}`.trim() }); setIsReminderOpen(false); }}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notes editor */}
      <Dialog open={isNotesOpen} onOpenChange={open => { if (!open) { setIsNotesOpen(false); setNotesCustomerId(null); setNotesValue(""); } }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader><DialogTitle>Notes — {customers.find(c => c.id === notesCustomerId)?.companyName}</DialogTitle></DialogHeader>
          <VoiceToTextTextarea value={notesValue} onChange={e => setNotesValue(e.target.value)} rows={12} className="mt-4 font-mono text-sm resize-y" autoFocus />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => { setIsNotesOpen(false); setNotesCustomerId(null); setNotesValue(""); }}>Cancel</Button>
            <Button onClick={() => { if (notesCustomerId) handleCellChange(notesCustomerId, "notes", notesValue); setIsNotesOpen(false); setNotesCustomerId(null); setNotesValue(""); }}>Save Notes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDeleteId} onOpenChange={open => { if (!open) setConfirmDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
            <AlertDialogDescription>This will soft-delete the CRM record. The linked workspace will not be affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground" onClick={() => { if (confirmDeleteId) handleDeleteWithUndo(confirmDeleteId); setConfirmDeleteId(null); }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete */}
      <Dialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete {selectedIds.size} Customers?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground py-4">This will soft-delete CRM records only. Linked workspaces are unaffected.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsBulkDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => bulkMutation.mutate({ ids: [...selectedIds], action: "delete" })} disabled={bulkMutation.isPending}>
              {bulkMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Delete {selectedIds.size}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk edit */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Edit {selectedIds.size} Customers</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Field</Label>
              <Select value={bulkEditField} onValueChange={setBulkEditField}>
                <SelectTrigger><SelectValue placeholder="Select field…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="region">Region</SelectItem>
                  <SelectItem value="businessType">Business Type</SelectItem>
                  <SelectItem value="rep">Rep</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="planTier">Plan Tier</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {bulkEditField && (
              <div className="space-y-2">
                <Label>Value</Label>
                {bulkEditField === "region" ? (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}><SelectTrigger><SelectValue placeholder="Select region…" /></SelectTrigger><SelectContent>{regions.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent></Select>
                ) : bulkEditField === "businessType" ? (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}><SelectTrigger><SelectValue placeholder="Select type…" /></SelectTrigger><SelectContent>{businessTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                ) : bulkEditField === "status" ? (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}><SelectTrigger><SelectValue placeholder="Select status…" /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
                ) : bulkEditField === "planTier" ? (
                  <Select value={bulkEditValue} onValueChange={setBulkEditValue}><SelectTrigger><SelectValue placeholder="Select plan…" /></SelectTrigger><SelectContent>{["lite", "pro", "enterprise"].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
                ) : (
                  <Input value={bulkEditValue} onChange={e => setBulkEditValue(e.target.value)} placeholder="Enter value…" />
                )}
              </div>
            )}
            <Button className="w-full" disabled={!bulkEditField || !bulkEditValue || bulkMutation.isPending} onClick={() => bulkMutation.mutate({ ids: [...selectedIds], action: "edit", fields: { [bulkEditField]: bulkEditValue } })}>
              {bulkMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Apply to {selectedIds.size} Customers
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge */}
      <Dialog open={isMergeOpen} onOpenChange={setIsMergeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge Customer Records</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-200">
              <strong>Note:</strong> This merges CRM contact data only. Both workspace accounts remain completely separate. Workspace consolidation is a separate process.
            </div>
            <div className="space-y-2">
              <Label>Keep this record as primary</Label>
              <Select value={mergeKeepId} onValueChange={setMergeKeepId}>
                <SelectTrigger data-testid="select-merge-keep"><SelectValue placeholder="Select primary record…" /></SelectTrigger>
                <SelectContent>
                  {mergeSelectedIds.map(id => {
                    const c = customers.find(x => x.id === id);
                    return c ? <SelectItem key={id} value={id}>{c.companyName} ({c.recordId || id.slice(0, 8)})</SelectItem> : null;
                  })}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              The other record's activities and any blank fields will be merged into the primary. The other record will be soft-deleted.
            </p>
            <Button
              className="w-full"
              disabled={!mergeKeepId || mergeMutation.isPending}
              onClick={() => {
                const mergeId = mergeSelectedIds.find(id => id !== mergeKeepId);
                if (mergeId) mergeMutation.mutate({ keepId: mergeKeepId, mergeId });
              }}
              data-testid="button-confirm-merge"
            >
              {mergeMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <GitMerge className="w-4 h-4 mr-2" /> Merge Records
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Customer */}
      <Dialog open={isAddCustomerOpen} onOpenChange={setIsAddCustomerOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Customer</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Workspace</Label>
              <Select value={newCustomerWorkspaceId} onValueChange={v => { setNewCustomerWorkspaceId(v); const ws = workspaces.find(w => w.id === v); if (ws && !newCustomerCompanyName) setNewCustomerCompanyName(ws.name); }}>
                <SelectTrigger data-testid="select-workspace"><SelectValue placeholder="Select workspace…" /></SelectTrigger>
                <SelectContent>
                  {workspaces.map(w => <SelectItem key={w.id} value={w.id}>{w.name} ({w.slug})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input value={newCustomerCompanyName} onChange={e => setNewCustomerCompanyName(e.target.value)} placeholder="Company name" data-testid="input-new-company" />
            </div>
            <Button
              className="w-full"
              disabled={!newCustomerWorkspaceId || !newCustomerCompanyName.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate({ workspaceId: newCustomerWorkspaceId, companyName: newCustomerCompanyName.trim() })}
              data-testid="button-confirm-add-customer"
            >
              {createMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
